import { Filament, Material, type FilamentList, formatBRL } from '@deizi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { z } from 'zod';
import { AffixInput } from '../../components/Form';
import { Alert } from '../../components/States';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { parseCentavos } from '../../lib/numbers';
import { StepHead, WizardFooter } from './WizardParts';

interface Row {
  brand: string;
  material: Material;
  name: string;
  price: string;
}

const SUGGESTIONS: Row[] = [
  { brand: 'Bambu Lab', material: 'PLA', name: 'PLA Basic Branco', price: '119,90' },
  { brand: 'Bambu Lab', material: 'PLA', name: 'PLA Basic Preto', price: '119,90' },
  { brand: 'Bambu Lab', material: 'PETG', name: 'PETG Transparente', price: '109,90' },
];
const CORAL: Row = {
  brand: 'Bambu Lab',
  material: 'PLA',
  name: 'PLA Basic Coral',
  price: '119,90',
};

/** Step 4 · Seus filamentos. */
export function StepFilaments({
  filaments,
  onBack,
  onNext,
}: {
  filaments: z.infer<typeof FilamentList>['data'];
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>(filaments.length === 0 ? [CORAL] : []);
  const valid = rows.filter((r) => r.name.trim() !== '' && (parseCentavos(r.price) ?? 0) > 0);
  const save = useMutation({
    mutationFn: async (toCreate: Row[]) => {
      for (const r of toCreate) {
        await api.post('/filaments', Filament, {
          brand: r.brand.trim() || 'Sem marca',
          material: r.material,
          name: r.name.trim(),
          pricePerKgCentavos: parseCentavos(r.price) ?? 0,
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['filaments'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
      onNext();
    },
  });
  const set = (i: number, patch: Partial<Row>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const total = filaments.length + valid.length;

  return (
    <>
      <StepHead title="Seus filamentos">
        Cadastre pelo menos um tipo com o preço pago por kg. Os carretéis (C-0001, C-0002…) entram
        depois, no estoque.
      </StepHead>
      {filaments.length > 0 ? (
        <section className="card col" style={{ gap: 4 }}>
          <h3>Já cadastrados</h3>
          {filaments.map((f) => (
            <div key={f.id} className="kv">
              <span>
                {f.name} <span className="xs muted">· {f.brand}</span>
              </span>
              <span className="v">{formatBRL(f.pricePerKgCentavos)}/kg</span>
            </div>
          ))}
        </section>
      ) : null}
      <section className="card flat">
        <div className="gt">
          <div
            className="gtr head stack-sm"
            style={{ gridTemplateColumns: '1fr 120px 1.4fr 150px 40px' }}
          >
            <span>Marca</span>
            <span>Material</span>
            <span>Nome e cor</span>
            <span>Preço por kg</span>
            <span />
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              className="gtr stack-sm"
              style={{ gridTemplateColumns: '1fr 120px 1.4fr 150px 40px' }}
            >
              <input
                className="cell"
                aria-label="Marca"
                value={r.brand}
                onChange={(e) => set(i, { brand: e.target.value })}
              />
              <select
                className="cell"
                aria-label="Material"
                value={r.material}
                onChange={(e) =>
                  set(i, { material: Material.options.find((m) => m === e.target.value) ?? 'PLA' })
                }
              >
                {Material.options.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <input
                className="cell"
                aria-label="Nome e cor"
                placeholder="Ex.: PLA Basic Coral"
                value={r.name}
                onChange={(e) => set(i, { name: e.target.value })}
              />
              <AffixInput
                ariaLabel="Preço por kg"
                prefix="R$"
                value={r.price}
                onChange={(v) => set(i, { price: v })}
              />
              <button
                type="button"
                className="btn sm g icon"
                aria-label="Remover filamento"
                onClick={() => setRows(rows.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          {rows.length === 0 && filaments.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}>
              <h3>Nenhum filamento ainda</h3>
              <p className="sm">O preço por kg é o que faz o custo do material sair certo.</p>
              <button type="button" className="btn sm" onClick={() => setRows([CORAL])}>
                Adicionar PLA Basic Coral · R$ 119,90/kg
              </button>
            </div>
          ) : null}
        </div>
      </section>
      <div className="row wrap" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            setRows([...rows, { brand: 'Bambu Lab', material: 'PLA', name: '', price: '' }])
          }
        >
          + Filamento
        </button>
        <span className="xs muted">Sugestões:</span>
        {SUGGESTIONS.map((s) => (
          <button key={s.name} type="button" className="chip" onClick={() => setRows([...rows, s])}>
            {s.name} · R$ {s.price}
          </button>
        ))}
      </div>
      <p className="xs muted">
        {total === 1 ? '1 tipo com preço' : `${total} tipos com preço`} · PLA Basic sai por volta de
        R$ 119,90/kg e PETG por R$ 109,90/kg. Use o preço com frete dividido pelo peso.
      </p>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <WizardFooter
        busy={save.isPending}
        blockMsg={total === 0 ? 'Adicione pelo menos um filamento com preço por kg.' : null}
        onBack={onBack}
        onSuggest={() =>
          save.mutate(filaments.length === 0 && valid.length === 0 ? [CORAL] : valid)
        }
        onContinue={() => save.mutate(valid)}
      />
    </>
  );
}
